import * as React from "react";
import { cn } from "@/lib/utils";

function Textarea({ className, onKeyDown, ...props }: React.ComponentProps<"textarea">) {
  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      // Handle Cmd+Backspace (or Cmd+Delete on Mac) to delete from cursor to line start
      if ((e.metaKey || e.ctrlKey) && (e.key === "Backspace" || e.key === "Delete")) {
        const textarea = e.currentTarget;
        const selectionStart = textarea.selectionStart;
        const selectionEnd = textarea.selectionEnd;

        // Find the start of the current line
        const value = textarea.value;
        const lineStart = value.lastIndexOf("\n", selectionStart - 1) + 1;

        if (lineStart < selectionStart || selectionEnd > 0) {
          e.preventDefault();

          // Delete from line start to cursor (or from line start to selection end if selection exists)
          const newValue = value.slice(0, lineStart) + value.slice(selectionEnd);

          // Update value
          const nativeEvent = { target: { ...textarea, value: newValue } };
          Object.defineProperty(nativeEvent, "target", { writable: false });

          // Use React's synthetic event system to update
          const event = new Event("input", { bubbles: true });
          textarea.value = newValue;
          textarea.dispatchEvent(event);

          // Set cursor position to where deletion started
          textarea.setSelectionRange(lineStart, lineStart);
        }
      }

      // Call the original onKeyDown if provided
      if (onKeyDown) {
        onKeyDown(e);
      }
    },
    [onKeyDown],
  );

  return (
    <textarea
      data-slot="textarea"
      className={cn(
        "border-border placeholder:text-muted-foreground flex field-sizing-content min-h-16 w-full rounded-md border bg-transparent px-3 py-2 text-base shadow-xs transition-[color,box-shadow] disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
        className,
      )}
      onKeyDown={handleKeyDown}
      {...props}
    />
  );
}

export { Textarea };
